import { Circle, Images, Mic, Square, Upload, Volume2, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AudioTrimmer } from './audio-trimmer';
import { useArmedDelete } from './block-actions';
import { BlockCard, BlockPanelCard } from './block-card';
import { IssueBar, type IssueContext, useIssueContext , type BlockIssue } from './block-issues';
import { BlockNameField } from './block-name-field';
import { LibraryModal } from './library-modal';
import { MediaOverlayControls } from './media-overlay-controls';
import { MediaPlaceholder } from './media-placeholder';
import { playerBoxHeight, WaveformPlayer } from './sound-waveform-player';
import { useIsMobile } from './task-content';
import { DisplayBankRow, DisplayScenarioBar, useDisplayPhoneTest, useDisplayVersions } from './use-display-versions';
import { formatTime } from './waveform-utils';


import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block } from '@/types/experience';
import type { SoundBlockData } from '@agavi/blocks';

import Toast from '@/components/toast';
import { TextField } from '@/components/ui/text-field';
import { useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { assetsApi, peekAsset, type AssetSummary, type AudioEditParams } from '@/lib/assets-api';
import { isNameTaken } from '@/lib/block-naming';

interface SoundBlockProps {
  block: Block;
  experienceId: string;
  taskId?: string;
  taskTitle?: string;
  blocks?: Block[];
  isPreview: boolean;
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  onRename?: (name: string) => void;
  /** The LITE shape — see `ImageBlock`'s own note; the two agree on what it drops. */
  condensed?: boolean;
}

const ALLOWED_MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/m4a'];
// Keep in sync with the backend's STORAGE_AUDIO_MAX_UPLOAD_BYTES / STORAGE_AUDIO_MAX_DURATION_SECONDS
// defaults — this is just an early, friendly client-side check; the server remains authoritative.
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const MAX_RECORD_SECONDS = 180;

/**
 * What VARIES between scenarios, and what is shared — §3.2's split.
 *
 * The clip and the words that stand in for it are the content, so they travel with the variant.
 * `loop` is how a clip PLAYS rather than which clip it is, so it stays on the block.
 */
const SOUND_VERSION_KEYS = ['assetId', 'transcript'] as const;

function toData(raw: Record<string, unknown>): SoundBlockData {
  return {
    assetId: (raw.assetId as string | null | undefined) ?? null,
    transcript: raw.transcript as string | undefined,
    loop: (raw.loop as boolean | undefined) ?? false,
  };
}

/** The transcript box, as `focusIssueField` addresses it. See Image's ALT_TEXT_FIELD. */
const TRANSCRIPT_FIELD = 'transcript';

// Missing transcript on a placed sound clip is a real accessibility error, not a warning — without
// it a screen-reader/deaf-or-hard-of-hearing student has no way to know what the clip even is
// (same standing rule Image's own alt-text check applies).
/** ONLY WHAT THIS BLOCK AUTHORED, for the reasons `imageIssues` sets out: a library caption is not
 *  this question's transcript, and the fallback was what made the check unreachable from outside. */
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `soundIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function soundBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return soundIssues(toData(block.data) as unknown as SoundBlockData, ctx);
}

export function soundIssues(d: SoundBlockData, ctx: IssueContext): BlockIssue[] {
  const { t } = ctx;
  if (!d.assetId || (d.transcript ?? '').trim()) return [];
  // `incompletion` for the same reason as Image's alt text: an absence, not a contradiction. Still
  // `severity: 'error'`, so it still blocks publication — only the timing changes.
  return [{ key: 'transcript-missing', fields: [TRANSCRIPT_FIELD], severity: 'error', kind: 'incompletion', category: t('blocks.sound.transcript'), detail: t('blocks.sound.missingTranscript') }];
}

// Same shape as every migrated input block's own top-issue bar (see e.g. ordering-block.tsx's
// orderingCollapsedIssueBar / mcq-block.tsx's mcqBarNode) — middle-aligned icon+text, text-sm, so
// this display block's own warning reads identically to theirs instead of the denser, top-aligned
// text-xs treatment `IssueRow` uses for a stacked multi-issue editor panel (a shape this block,
// with only ever one possible issue, never needs).
interface PendingClip {
  file: File;
  previewUrl: string;
  sourceDuration: number;
  source: 'upload' | 'recording';
}

function stripExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx > 0 ? filename.slice(0, idx) : filename;
}

export default function SoundBlock({ block, experienceId, taskId = '', taskTitle = '', blocks = [], isPreview, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, onRename, condensed }: SoundBlockProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const issueCtx = useIssueContext(block.id, blocks);
  const stored = toData(block.data);
  /* Declared before the bank hook, which reads it: the variant in force depends on whether this
     instance is the editor or the card. Seeded from the STORED clip, since there is no variant
     resolved yet at this point and "has this block got anything in it" is the same either way. */
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !stored.assetId));
  const taskPreviewRoll = useTaskPreviewRoll();
  /* The bank and its three-way "which variant" rule, shared with Text, Image and DataViz. */
  const {
    bank, leadsGroup, shown, shownVersionId, setTestVersion,
    editorVersionId, setEditorVersion, writeFields, addVariant, removeVariant, renameVariant, removeWarning,
  } = useDisplayVersions(block, SOUND_VERSION_KEYS, onUpdate, {
    isPreview, isEditing, taskPreviewRoll, blocks,
    nameFor: index => t('blocks.versions.scenarioDefault', { index }),
    warnFor: (names: string[]) => t('blocks.scenario.removeWarning', { names: names.join(', ') }),
    base: stored as unknown as Record<string, unknown>,
  });

  /*
   * ABOVE EVERY EARLY RETURN. `isPreview` and friends bail out further down, so declaring these
   * beside `usePortal` meant the hooks ran only on some renders — which is a crash the moment a
   * block flips into Preview, not only a lint complaint.
   */
  const [phoneMode, setPhoneMode] = useState<'edit' | 'test'>('edit');
  const usePortal = isMobile && !embedded;
  /* Edit/Test on the phone, the surface Text already had. Hidden rather than unmounted so the
     editor keeps its state across a tab flip. See `useDisplayPhoneTest`. */
  const phoneTest = useDisplayPhoneTest({
    block, bank, shownVersionId, setTestVersion, phoneMode, setPhoneMode, enabled: usePortal,
  });
  /** Everything below reads the VARIANT IN FORCE, never the block's own vestigial copy. */
  const data = shown as unknown as SoundBlockData;
  const missingTranscriptWarningId = useId();
  useLayoutEffect(() => {
    if (mobileIsEditing === undefined) return;
    setIsEditing(mobileIsEditing);
  }, [mobileIsEditing]);

  // Refocuses the collapsed card once the editor closes — mirrors the collapsedContentRef pattern
  // already used by mcq-block.tsx/dropdown-block.tsx/slider-block.tsx, which this display block
  // (hand-rolled like Image, not built on the shared input-block frame) didn't have yet.
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const stageRegionRef = useRef<HTMLDivElement>(null);

  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Seeded from the cache so a clip already in memory is there in the first frame — same reason
   *  as the image block's, which has the longer note. */
  const [cachedAsset, setCachedAsset] = useState<AssetSummary | null>(
    () => (data.assetId ? peekAsset(data.assetId) ?? null : null),
  );
  /** The fetch came back empty-handed. Distinct from "not yet", which looks identical in state and
   * means the opposite to whoever is waiting. */
  const [assetFailed, setAssetFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  // Recording — a two-stage arm/start so a teacher can position themselves (and the room can quiet
  // down) before capture actually begins, instead of the mic going live the instant Record is tapped.
  const [recordingArmed, setRecordingArmed] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Pending upload/recording review — trim/normalize/fade (and the display name) are set here,
  // once, before the actual upload call; there is no later "re-edit a stored clip" action.
  const [pendingClip, setPendingClip] = useState<PendingClip | null>(null);
  const pendingClipRef = useRef<PendingClip | null>(null);
  useEffect(() => { pendingClipRef.current = pendingClip; }, [pendingClip]);
  const [pendingName, setPendingName] = useState('');
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [normalize, setNormalize] = useState(false);
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);

  // The recording arm→start→stop→review flow removes the exact control that was just pressed to
  // advance each stage — same shape of problem library-modal.tsx's swap flow already solves with
  // its own swapRegionRef. Falls back to this region if a stage transition would otherwise drop
  // focus to document.body.
  useEffect(() => {
    if (document.activeElement === document.body) stageRegionRef.current?.focus();
  }, [pendingClip, recordingActive, recordingArmed]);

  // Lazily resolve the placed asset (fresh signed URL + inherited description) whenever the
  // block only has an assetId persisted (e.g. on first load, or after a pick).
  useEffect(() => {
    if (!data.assetId) { setCachedAsset(null); setAssetFailed(false); return; }
    if (cachedAsset?.id === data.assetId) return;
    let cancelled = false;
    setAssetFailed(false);
    assetsApi.get(data.assetId)
      .then((asset) => { if (!cancelled) setCachedAsset(asset); })
      // The toast is for the editor. Preview has no toast of its own and, before the placeholder
      // below, no block either — so a failure there was entirely silent.
      .catch(() => { if (!cancelled) { setAssetFailed(true); setToastMsg(t('blocks.sound.loadError')); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.assetId]);

  // Re-fetches the placed asset to get a freshly-signed audio URL — passed to the waveform
  // player(s) below as onRetry, since a load failure there is most likely the 1-hour signed URL
  // simply expiring mid-editing-session, which re-fetching the SAME url can never recover from.
  function refreshCachedAsset() {
    if (!data.assetId) return;
    setAssetFailed(false);
    assetsApi.get(data.assetId)
      .then(setCachedAsset)
      .catch(() => { setAssetFailed(true); setToastMsg(t('blocks.sound.loadError')); });
  }

  function revokePendingUrl(clip: PendingClip | null) {
    if (clip) URL.revokeObjectURL(clip.previewUrl);
  }

  // Shared by both the file-picker path and the just-finished-recording path — validates, stages
  // the file for review (waveform trim/fade + a listen-back), and probes its duration client-side
  // via a throwaway <audio> so trimEnd can default to "the whole clip". Uploads default the review
  // name to their own filename (already meaningful); recordings — which otherwise get an opaque
  // auto-generated name — default to the task title plus a timestamp instead.
  function stageForReview(file: File, source: 'upload' | 'recording') {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setToastMsg(t('blocks.sound.unsupportedFileType'));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setToastMsg(t('blocks.sound.fileTooLarge', { maxMB: Math.round(MAX_UPLOAD_BYTES / (1024 * 1024)) }));
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    const probe = new Audio(previewUrl);
    probe.addEventListener('loadedmetadata', () => {
      const duration = probe.duration || 0;
      // Uploads (unlike live recording, which is capped by its own timer) had no client-side
      // duration check before this — a teacher could sit through a full upload + server-side
      // transcode only to be rejected at the very end. Catch it here instead.
      if (duration > MAX_RECORD_SECONDS) {
        URL.revokeObjectURL(previewUrl);
        setToastMsg(t('blocks.sound.fileTooLong', { max: formatTime(MAX_RECORD_SECONDS) }));
        return;
      }
      setPendingClip({ file, previewUrl, sourceDuration: duration, source });
      setPendingName(
        source === 'recording'
          ? `${taskTitle || t('blocks.sound.title')} – ${new Date().toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}`
          : stripExtension(file.name),
      );
      setTrimStart(0);
      setTrimEnd(duration);
      setNormalize(false);
      setFadeIn(0);
      setFadeOut(0);
    }, { once: true });
  }

  function discardPendingClip() {
    revokePendingUrl(pendingClip);
    setPendingClip(null);
  }

  async function confirmPendingClip() {
    if (!pendingClip) return;
    setUploading(true);
    try {
      const editParams: AudioEditParams = {};
      if (trimStart > 0) editParams.trimStartSeconds = trimStart;
      if (trimEnd < pendingClip.sourceDuration - 0.05) editParams.trimEndSeconds = trimEnd;
      if (normalize) editParams.normalize = true;
      if (fadeIn > 0) editParams.fadeInSeconds = fadeIn;
      if (fadeOut > 0) editParams.fadeOutSeconds = fadeOut;

      const result = await assetsApi.uploadAudio(pendingClip.file, editParams);
      let uploaded: AssetSummary = result;
      const trimmedName = pendingName.trim();
      if (trimmedName && trimmedName !== uploaded.filename) {
        uploaded = await assetsApi.update(uploaded.id, { filename: trimmedName });
      }
      setCachedAsset(uploaded);
      set({ assetId: uploaded.id, transcript: undefined });
      if (result.deduped) setToastMsg(t('blocks.sound.library.deduped'));
      revokePendingUrl(pendingClip);
      setPendingClip(null);
    } catch (err) {
      setToastMsg(err instanceof Error ? err.message : t('blocks.sound.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) stageForReview(file, 'upload');
  }

  function handleLibrarySelect(asset: AssetSummary, chosen: { transcript?: string }) {
    setCachedAsset(asset);
    set({ assetId: asset.id, transcript: chosen.transcript });
    setShowLibraryModal(false);
  }

  function handleClear() {
    setCachedAsset(null);
    set({ assetId: null, transcript: undefined });
  }

  /* Through the version being edited when there is a bank, and merged onto the WHOLE stored
     object either way — `onUpdate` replaces `block.data`, so a write naming only what changed
     would delete the bank on the first edit. */
  function set(patch: Partial<SoundBlockData>) {
    writeFields(patch as Record<string, unknown>);
  }

  function handleDone() {
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  function stopRecordingStream() {
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  }

  // Stage 1 — request the mic and go to standby ("armed"), but don't capture anything yet. Gives a
  // teacher a beat to position themselves/the room before audio is actually recorded.
  async function armRecording() {
    setRecordingError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      setRecordingArmed(true);
    } catch (err) {
      setRecordingError(err instanceof Error ? err.message : t('blocks.sound.micError'));
      setRecordingArmed(true); // still show the standby panel so the error + retry are visible
    }
  }

  // Stage 2 — actually start capturing from the already-armed stream.
  function beginRecording() {
    const stream = recordingStreamRef.current;
    if (!stream) return;
    recordedChunksRef.current = [];
    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      const file = new File([blob], `recording-${Date.now()}.webm`, { type: blob.type });
      stopRecordingStream();
      setRecordingArmed(false);
      setRecordingActive(false);
      stageForReview(file, 'recording');
    };
    recorder.start();
    setRecordingElapsed(0);
    setRecordingArmed(false);
    setRecordingActive(true);
    recordingTimerRef.current = setInterval(() => {
      setRecordingElapsed((prev) => {
        const next = prev + 1;
        if (next >= MAX_RECORD_SECONDS) stopRecording();
        return next;
      });
    }, 1000);
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
  }

  function cancelRecording() {
    stopRecordingStream();
    setRecordingArmed(false);
    setRecordingActive(false);
    mediaRecorderRef.current = null;
  }

  useEffect(() => () => { stopRecordingStream(); revokePendingUrl(pendingClipRef.current); }, []);

  /* THIS BLOCK'S OWN — see `imageIssues` for why a library description may not stand in for one
     written here, and where a description from another placement is allowed to enter instead. */
  const effectiveTranscript = data.transcript ?? '';
  const issues = soundIssues(data, issueCtx);
  const missingTranscript = issues.length > 0;

  if (isPreview) {
    // Nothing placed at all is the one case that stays empty: a student can never meet a block with
    // no clip in it, the editor already calls that an error, and a "no sound yet" note is authoring
    // scaffolding rather than anything a student should read.
    if (!data.assetId) return null;
    return cachedAsset ? (
      // `data-media-transcript` lets Preview's accessibility report see what the editor already
      // errors on. A player with no transcript is a real barrier and it is the teacher's to fix —
      // the same shape as an image with no alt text.
      <figure className='w-full' data-media-transcript={effectiveTranscript.trim() ? '1' : ''}>
        <WaveformPlayer src={cachedAsset.urls.audio} t={t} loop={data.loop} onRetry={refreshCachedAsset} />
        {effectiveTranscript && (
          <figcaption className='mt-2 text-xs text-ws-muted'>{effectiveTranscript}</figcaption>
        )}
      </figure>
    ) : (
      // The player's OWN height, asked of the player — Preview's takes its defaults, so this
      // reserves 174px rather than the 64 the placeholder falls back to. Sound was leaving the
      // same hole Image was; the note on `boxStyle` has the measurements.
      <MediaPlaceholder
        boxStyle={{ height: playerBoxHeight() }}
        message={t(assetFailed ? 'blocks.sound.loadError' : 'blocks.sound.loading')}
        failed={assetFailed}
        retryLabel={t('blocks.sound.tryAgain')}
        onRetry={refreshCachedAsset}
      />
    );
  }

  if (!isEditing) {
    const ariaLabel = cachedAsset?.filename ? t('blocks.sound.editAriaWithFile', { fileName: cachedAsset.filename }) : t('blocks.sound.editAriaNoSound');
    const contentProps = {
      tabIndex: 0 as const,
      role: 'button' as const,
      'aria-label': ariaLabel,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isDeleteArmed) { disarmDelete(); return; }
          if (onSelect) onSelect(); else setIsEditing(true);
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.detail === 0) return;
        if (isDeleteArmed) { disarmDelete(); return; }
        if (onSelect) onSelect(); else setIsEditing(true);
      },
    };
    // This collapsed card is the canonical student-facing display, so the discrete transport
    // controls (Play, skip, speed) all stay real — only the waveform's own scrub drag is disabled,
    // since a drag gesture there would eat most of the card's own "click to edit" hit area. The
    // waveform itself is ghosted to signal it isn't scrubbable in this view.
    const contentChildren = (
      <>
        {/* The same gate Text and Image use: on selection, and on a CLOSED card only while this
            block is actually leading a group. `leadsGroup` requires a real bank. */}
        {(isSelected || leadsGroup) && bank.length > 1 && (
          <DisplayScenarioBar
            bank={bank}
            activeId={shownVersionId}
            onSelect={setTestVersion}
            /* NO DICE. A reroll rolls VARIABLES and never the variant — the pager already
               steps scenarios, deliberately — and this block holds none. Only full Preview
               draws a scenario at random. See `DisplayScenarioBar.onReroll`. */
          />
        )}
        {data.assetId && cachedAsset ? (
          <WaveformPlayer src={cachedAsset.urls.audio} t={t} height={40} interactive={false} loop={data.loop} onRetry={refreshCachedAsset} />
        ) : data.assetId ? (
          /*
           * The same reserved box Preview uses, sized from the player beside it.
           *
           * A line of italic text stands ~20px where this card's player stands 150, so the card
           * collapsed and sprang open every time an asset arrived. `height: 40` is what the
           * player above is given; `playerBoxHeight` adds the rows around it, including the speed
           * row that `showSpeed`'s default puts there and the call site never mentions.
           *
           * Not always "Loading", as before: a failed fetch left this saying so indefinitely,
           * which is the same silence as Preview's missing block wearing a more reassuring face.
           * `MediaPlaceholder` also carries the retry the bare paragraph never had.
           */
          <MediaPlaceholder
            boxStyle={{ height: playerBoxHeight({ height: 40 }) }}
            message={t(assetFailed ? 'blocks.sound.loadError' : 'blocks.sound.loading')}
            failed={assetFailed}
            retryLabel={t('blocks.sound.tryAgain')}
            onRetry={refreshCachedAsset}
          />
        ) : (
          <p className='text-sm italic text-ws-muted'>{t('blocks.sound.empty')}</p>
        )}
        {/* Just enough to confirm a transcript exists, not the transcript itself — the full text
            belongs in the editor and the actual student-facing display (both untruncated). */}
        {effectiveTranscript && <p className='mt-2 truncate text-xs text-ws-muted'>{effectiveTranscript}</p>}
      </>
    );

    if (isMain) {
      return (
        <BlockCard
          block={block}
          cardRef={blockRef}
          icon={<Volume2 className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
          isDeleteArmed={isDeleteArmed}
          isSelected={isSelected}
          isDragOver={isDragOver}
          onDuplicate={triggerDuplicate}
          onDelete={triggerDelete}
          onDisarm={disarmDelete}
        >
          <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
            <div ref={collapsedContentRef} {...contentProps} className='relative flex-1 cursor-pointer p-4 pb-8 focus-visible:[outline:2px_auto]'>
              {contentChildren}
            </div>
            {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='border-t border-ws-border' />}
          </div>
        </BlockCard>
      );
    }

    return (
      <BlockPanelCard
        blockId={block.id}
        cardRef={blockRef}
        icon={<Volume2 className='h-6 w-6' strokeWidth={2} />}
        title={t('blocks.sound.title')}
        isDeleteArmed={isDeleteArmed}
        isSelected={isSelected}
        isDragOver={isDragOver}
        onDuplicate={triggerDuplicate}
        onDelete={triggerDelete}
        onDisarm={disarmDelete}
      >
        <div ref={collapsedContentRef} {...contentProps} className={`relative p-4 pb-8 cursor-pointer focus-visible:[outline:2px_auto] ${missingTranscript ? '' : 'rounded-b-xl'}`}>
          {contentChildren}
        </div>
        {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='rounded-b-xl' />}
      </BlockPanelCard>
    );
  }


  /** One control, rendered in whichever column has the room — see its placement note below. */
  const loopControl = (
    <label className='flex items-center gap-2 text-sm text-ws-text'>
      <input type='checkbox' checked={!!data.loop} onChange={(e) => set({ loop: e.target.checked })} className='h-4 w-4 rounded border-ws-border' />
      {t('blocks.sound.loop')}
    </label>
  );

  const editView = (
    <div className={usePortal
      ? 'fixed inset-x-0 top-0 z-[100] flex flex-col bg-ws-surface h-[100dvh]'
      : condensed
        ? 'flex flex-col'
        : 'flex h-full flex-col rounded-xl border border-ws-border bg-ws-surface'
    }>
      {/* Header — dropped when condensed, for the reasons `ImageBlock` states. */}
      {!condensed && (
      <div className={`flex items-center gap-2 border-b border-ws-border bg-ws-hover px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link${!usePortal ? ' rounded-t-xl' : ''}`}>
        <Volume2 className='h-6 w-6' strokeWidth={2} />
        <BlockNameField
          blockName={block.name ?? ''}
          titleLabel={t('blocks.sound.title')}
          onBlockNameChange={onRename}
          isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
        />
        <button
          onClick={handleDone}
          aria-label={t('blocks.sound.closeEditor')}
          className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'
        >
          <X className='h-5 w-5' />
        </button>
      </div>
      )}
      {/* tabIndex=-1 fallback focus target — see the stageRegionRef effect above: each stage of
          the recording flow (armed → active → review → placed) removes the control that was just
          clicked to advance it, so focus needs somewhere to land instead of dropping to body. */}
      {phoneTest?.tabs}
      {phoneTest?.pane}
      <div ref={stageRegionRef} tabIndex={-1} className={`flex flex-1 flex-col outline-none ${condensed ? 'gap-4 p-3' : 'gap-6 p-6'}${usePortal ? ' overflow-y-auto' : ''}${phoneTest?.pane ? ' hidden' : ''}`}>
        {/* THE BANK, first in the body — a variant decides which clip this is. Absent when
            condensed: an exit is read after the flow has routed, so nothing steps a variant. */}
        {!condensed && (
        <DisplayBankRow
          bank={bank}
          editorVersionId={editorVersionId}
          setEditorVersion={setEditorVersion}
          addVariant={addVariant}
          removeVariant={removeVariant}
          renameVariant={renameVariant}
          removeWarning={removeWarning}
        />
        )}
        <div aria-live='polite' aria-atomic='true' className='sr-only'>
          {recordingError ? recordingError
            : recordingArmed ? t('blocks.sound.readyToRecord')
            : recordingActive ? t('blocks.sound.recordingInProgress')
            : ''}
        </div>

      {data.assetId ? (
        /*
         * Two columns when condensed — the clip and how it plays on one side, the transcript on
         * the other. `ImageBlock`'s own note has the measurement and the reasoning; the two
         * blocks split on the same line, between the asset and the writing about it.
         *
         * WIDER THAN IMAGE'S COLUMN, and not by taste. A picture has no intrinsic width — it is
         * told a height and scales — so 15rem suits it. This player does: its first row is skip,
         * play, skip and an elapsed/duration readout, about 190px before the waveform gets any,
         * and the swap/clear pair floats over the top-right corner of the same box. At 15rem the
         * circles landed on the readout. The column is sized so the overlay clears the longest
         * row rather than the overlay being moved, because that pair sits in the same corner on
         * both blocks and a teacher should not have to look for it in two places.
         */
        <div className={condensed
          ? 'grid gap-x-4 gap-y-3 sm:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]'
          : 'contents'}>
        <div className={condensed ? 'flex min-w-0 flex-col gap-3' : 'contents'}>
            {/* Placed clip — real interactive player (play/pause/scrub/speed), so it can't also
                double as a single "click to swap" target. Image reached the same conclusion once its
                own content became interactive, and the two now share one control. */}
            <div className='relative'>
              <WaveformPlayer src={cachedAsset?.urls.audio} t={t} loop={data.loop} onRetry={refreshCachedAsset} />
              <MediaOverlayControls
                onSwap={() => setShowLibraryModal(true)}
                swapLabel={t('blocks.sound.openLibraryToSwap')}
                onClear={handleClear}
                clearLabel={t('blocks.sound.clear')}
              />
            </div>

            {/* UNDER THE PLAYER on the canvas, under the TRANSCRIPT when condensed — the same
                control, placed where the space is. The two columns are as tall as the taller of
                them, and the player is ~150px against a two-row transcript's ~76: the right
                column had room going spare and the left had none, so a checkbox sitting under
                the player was adding to the row's height for free. Moved across, it costs
                nothing and the transcript gets the difference. */}
            {!condensed && loopControl}

        </div>
        <div className={condensed ? 'flex min-w-0 flex-col gap-3' : 'contents'}>
            {/* Transcript — grows to fill whatever room is left in the editor panel (there's
                usually plenty below a short player + one checkbox), rather than a fixed 4-row box
                with empty space sitting unused underneath it.

                NOT TOLD TO GROW when condensed. That reasoning is about a panel with room
                under it; a dialog row has none, and a box that expands inside a column which is
                itself one of five made the tallest thing on the surface. A fixed floor there
                instead, which still grows with what is typed as every prose box does.

                The floor is set against the OTHER column rather than picked: two columns are as
                tall as the taller of them, and the player is about 150px. A transcript shorter
                than that bought no height back, it only left the right column half empty — so
                it takes the room the player is spending anyway. */}
            <div className={condensed ? 'flex flex-col' : 'flex flex-1 flex-col'}>
              <label className='mb-1.5 flex items-center gap-1 text-sm font-medium text-ws-text'>
                {t('blocks.sound.transcript')}
                <span className='text-ws-muted font-normal'>{t('blocks.sound.transcriptRequired')}</span>
              </label>
              <TextField
                variant='prose'
                // `rows` picks the shape; the flex classes keep the box growing into whatever room is
                // left in the panel, which is what this field did before and worth keeping.
                rows={4}
                fieldId={TRANSCRIPT_FIELD}
                value={effectiveTranscript}
                onChange={v => set({ transcript: v })}
                placeholder={t('blocks.sound.transcriptPlaceholder')}
                hasError={missingTranscript}
                ariaLabel={t('blocks.sound.transcript')}
                describedBy={missingTranscript ? missingTranscriptWarningId : undefined}
                className={condensed ? 'min-h-24' : 'min-h-32 flex-1'} />
              {/* Not visible: the collapsed card's issue bar is the one place this is SHOWN, so a
                  second copy under the field is the duplication this pass removes. It stays in the
                  accessibility tree because `aria-invalid` on its own says a field is wrong without
                  saying why, and the bar is elsewhere in the tree. */}
              {missingTranscript && (
                <span id={missingTranscriptWarningId} className='sr-only'>{t('blocks.sound.missingTranscript')}</span>
              )}
            </div>
            {condensed && loopControl}
        </div>
        </div>
      ) : pendingClip ? (
        <div className='flex flex-col gap-4'>
          <AudioTrimmer
            file={pendingClip.file}
            duration={pendingClip.sourceDuration}
            trimStart={trimStart}
            trimEnd={trimEnd}
            fadeIn={fadeIn}
            fadeOut={fadeOut}
            onTrimChange={(s, e) => { setTrimStart(s); setTrimEnd(e); }}
            onFadeChange={(fi, fo) => { setFadeIn(fi); setFadeOut(fo); }}
            t={t}
          />

          <div className='flex flex-col'>
            <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.sound.assetName')}</label>
            <TextField
              variant='prose'
              value={pendingName}
              onChange={setPendingName}
              ariaLabel={t('blocks.sound.assetName')} />
          </div>

          <label className='flex items-center gap-2 text-sm text-ws-text'>
            <input type='checkbox' checked={normalize} onChange={(e) => setNormalize(e.target.checked)} className='h-4 w-4 rounded border-ws-border' />
            {t('blocks.sound.normalizeVolume')}
          </label>

          <div className='flex items-center justify-end gap-2'>
            <button
              type='button'
              onClick={discardPendingClip}
              disabled={uploading}
              className='rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover disabled:opacity-50'
            >
              {t('blocks.sound.discardRecording')}
            </button>
            <button
              type='button'
              onClick={() => void confirmPendingClip()}
              disabled={uploading}
              className='rounded-md bg-ws-primary px-3 py-2 text-sm font-medium text-ws-btn-on-primary hover:opacity-90 disabled:opacity-50'
            >
              {uploading ? t('blocks.sound.uploading') : t('blocks.sound.applyEdits')}
            </button>
          </div>
        </div>
      ) : recordingActive ? (
        <div className='flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-ws-border py-10 px-4 text-center'>
          <div className='flex items-center gap-2 text-ws-warning-fg'>
            <Circle className='h-3 w-3 animate-pulse fill-current' />
            <span className='text-sm font-medium'>{t('blocks.sound.recordingInProgress')}</span>
          </div>
          <p className='text-2xl font-semibold tabular-nums text-ws-text'>{formatTime(recordingElapsed)}</p>
          <p className='text-xs text-ws-muted'>{t('blocks.sound.recordingElapsed', { max: formatTime(MAX_RECORD_SECONDS) })}</p>
          <div className='flex items-center gap-3'>
            <button type='button' onClick={cancelRecording} className='rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover'>
              {t('common.cancel')}
            </button>
            <button
              type='button'
              onClick={stopRecording}
              aria-label={t('blocks.sound.stopRecording')}
              className='flex h-14 w-14 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary shadow-lg hover:opacity-90'
            >
              <Square className='h-5 w-5' />
            </button>
          </div>
        </div>
      ) : recordingArmed ? (
        <div className='flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-ws-border py-10 px-4 text-center'>
          {recordingError ? (
            <>
              <Mic className='h-7 w-7 text-ws-muted' />
              <p role='alert' className='text-sm text-ws-text'>{t('blocks.sound.micUnavailable')}</p>
              <p className='text-xs text-ws-muted'>{recordingError}</p>
              <div className='flex items-center gap-3'>
                <button type='button' onClick={cancelRecording} className='rounded-md border border-ws-border px-3 py-1.5 text-xs font-medium text-ws-text hover:bg-ws-hover'>
                  {t('common.cancel')}
                </button>
                <button onClick={() => void armRecording()} className='rounded-md border border-ws-border px-3 py-1.5 text-xs font-medium text-ws-text hover:bg-ws-hover'>
                  {t('blocks.sound.tryAgain')}
                </button>
              </div>
            </>
          ) : (
            <>
              <Mic className='h-7 w-7 text-ws-muted' />
              <p className='text-sm text-ws-text'>{t('blocks.sound.readyToRecord')}</p>
              <p className='text-xs text-ws-muted'>{t('blocks.sound.readyToRecordHint')}</p>
              <div className='flex items-center gap-3'>
                <button type='button' onClick={cancelRecording} className='rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover'>
                  {t('common.cancel')}
                </button>
                <button
                  type='button'
                  onClick={beginRecording}
                  aria-label={t('blocks.sound.startRecording')}
                  className='flex h-14 w-14 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary shadow-lg hover:opacity-90'
                >
                  <Circle className='h-5 w-5 fill-current' />
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          /* Condensed drops the dashed panel and the format list, for the reasons `ImageBlock`'s
             own empty state states. The two blocks agree here as they do everywhere else. */
          className={condensed
            ? 'flex flex-wrap items-center gap-2'
            : 'flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-ws-border py-8 px-4'}
        >
          <div className='flex gap-3'>
            <button
              type='button'
              onClick={() => setShowLibraryModal(true)}
              className='flex flex-col items-center gap-1.5 rounded-lg border border-ws-border bg-ws-btn px-4 py-3 text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            >
              <Images className='h-5 w-5' />
              <span className='text-xs font-medium'>{t('blocks.sound.tabs.library')}</span>
            </button>
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              className='flex flex-col items-center gap-1.5 rounded-lg border border-ws-border bg-ws-btn px-4 py-3 text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            >
              <Upload className='h-5 w-5' />
              <span className='text-xs font-medium'>{t('blocks.sound.tabs.upload')}</span>
            </button>
            <button
              type='button'
              onClick={() => void armRecording()}
              className='flex flex-col items-center gap-1.5 rounded-lg border border-ws-border bg-ws-btn px-4 py-3 text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            >
              <Mic className='h-5 w-5' />
              <span className='text-xs font-medium'>{t('blocks.sound.tabs.record')}</span>
            </button>
          </div>
          {!condensed && <span className='text-xs text-ws-muted'>{t('blocks.sound.supported')}</span>}
        </div>
      )}

      <input
        ref={fileInputRef}
        type='file'
        accept='audio/*'
        className='sr-only'
        /* OUT OF THE TAB ORDER. `sr-only` hides it from sight but NOT from the keyboard, so
           this sat in the tab order as a silent extra stop beside the visible control that
           opens it: a file input with no label, no `aria-label` and nothing to announce. It
           is driven by a ref from that control and is not the accessible affordance — the
           button is. (`hidden`, which the avatar and CSV pickers use, has never had this
           problem: `display:none` is not focusable.) */
        tabIndex={-1}
        onChange={handleFileChange}
      />
      </div>
      {toastMsg && <Toast message={toastMsg} type='error' onDismiss={() => setToastMsg('')} />}
      {showLibraryModal && (
        <LibraryModal
          assetKind='audio'
          experienceId={experienceId}
          currentAssetId={data.assetId}
          currentTaskId={taskId}
          currentTaskBlocks={blocks}
          onSelect={handleLibrarySelect}
          onClose={() => setShowLibraryModal(false)}
        />
      )}
    </div>
  );

  return usePortal ? createPortal(editView, document.body) : editView;
}
